import {
  Button,
  Empty,
  Image,
  message,
  Spin,
  Tag
} from "antd";

import {
  ReloadOutlined,
  ScanOutlined,
  WarningOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  SendOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined
} from "@ant-design/icons";

import {
  useEffect,
  useState
} from "react";

import dayjs from "dayjs";

import axiosInstance
  from "../../../utils/axios";


const money = value =>
  `${Number(value || 0).toLocaleString("vi-VN")}đ`;


const issueInfo = type => {

  switch (type) {

    case "duplicate_image":
      return {
        label: "Ảnh bị trùng",
        color: "red"
      };

    case "amount_mismatch":
      return {
        label: "Sai số tiền",
        color: "orange"
      };

    case "ocr_unreadable":
      return {
        label: "Không đọc được tiền",
        color: "gold"
      };

    case "ocr_error":
      return {
        label: "OCR lỗi",
        color: "volcano"
      };

    default:
      return {
        label: "Cần kiểm tra",
        color: "default"
      };
  }
};


export default function PaymentAuditManage() {

  const [data, setData] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [scanning, setScanning] =
    useState(false);

  const [processing, setProcessing] =
    useState({});


  const fetchData = async () => {

    setLoading(true);

    try {

      const res =
        await axiosInstance.get(
          "/order/payment-audit/issues"
        );

      setData(
        Array.isArray(
          res?.data?.data
        )
          ? res.data.data
          : []
      );

    } catch (error) {

      console.error(error);

      message.error(
        "Không tải được danh sách kiểm tra!"
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    fetchData();

  }, []);


  const scanHistory = async () => {

    setScanning(true);

    try {

      const res =
        await axiosInstance.post(
          "/order/payment-audit/scan-history",
          {
            limit: 10
          },
          {
            timeout: 180000
          }
        );

      message.success(
        `Đã quét ${res?.data?.scanned || 0} đơn - ${res?.data?.issues || 0} cảnh báo`
      );

      await fetchData();

    } catch (error) {

      console.error(error);

      message.error(
        "Quét chưa hoàn tất hoặc OCR mất quá nhiều thời gian."
      );

    } finally {

      setScanning(false);
    }
  };


  const copyText = async (
    text,
    successText
  ) => {

    try {

      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {

        await navigator.clipboard.writeText(
          String(text || "")
        );

      } else {

        const el =
          document.createElement(
            "textarea"
          );

        el.value =
          String(text || "");

        el.style.position =
          "fixed";

        el.style.opacity =
          "0";

        document.body.appendChild(
          el
        );

        el.focus();
        el.select();

        document.execCommand(
          "copy"
        );

        document.body.removeChild(
          el
        );
      }

      message.success(
        successText
      );

    } catch {

      message.error(
        "Không sao chép được"
      );
    }
  };


  const changeStatus = async (
    item,
    status
  ) => {

    const orderId =
      item?.order_id;


    /*
     * Optimistic UI:
     * bam la card bien mat ngay.
     */
    setData(
      current =>
        current.filter(
          row =>
            Number(row?.order_id) !==
            Number(orderId)
        )
    );


    setProcessing(
      current => ({
        ...current,
        [orderId]: status
      })
    );


    const text =
      status === 1
        ? "Thành công"
        : status === 2
          ? "Đang gửi hàng"
          : "Đã hủy";


    message.success(
      `Đơn #${orderId} → ${text}`,
      1
    );


    try {

      await axiosInstance.post(
        `/order/updateByStatus/${orderId}`,
        {
          status
        }
      );

    } catch (error) {

      console.error(
        "UPDATE STATUS ERROR:",
        error
      );


      /*
       * API loi:
       * dua card quay lai.
       */
      setData(
        current => {

          const existed =
            current.some(
              row =>
                Number(row?.order_id) ===
                Number(orderId)
            );

          if (existed) {
            return current;
          }

          return [
            item,
            ...current
          ].sort(
            (a, b) =>
              Number(b?.order_id || 0) -
              Number(a?.order_id || 0)
          );
        }
      );


      message.error(
        `Đơn #${orderId} cập nhật thất bại, đã đưa lại vào danh sách.`
      );

    } finally {

      setProcessing(
        current => {

          const next = {
            ...current
          };

          delete next[orderId];

          return next;
        }
      );
    }
  };


  return (

    <div
      className="
        min-h-screen

        bg-[#f5f7fa]

        p-3
        md:p-5
        lg:p-6
      "
    >

      {/* HEADER */}

      <div
        className="
          bg-white

          rounded-2xl

          border
          border-gray-100

          shadow-sm

          p-4

          mb-3
        "
      >

        <div
          className="
            flex
            flex-col

            sm:flex-row
            sm:items-center
            sm:justify-between

            gap-3
          "
        >

          <div>

            <h1
              className="
                m-0

                text-[20px]

                font-bold

                text-gray-900
              "
            >
              Kiểm tra chuyển khoản
            </h1>


            <div
              className="
                mt-1

                text-xs
                text-gray-500
              "
            >
              {data.length} đơn cần kiểm tra
            </div>

          </div>


          <div
            className="
              grid
              grid-cols-2

              gap-2
            "
          >

            <Button
              icon={
                <ReloadOutlined />
              }

              loading={loading}

              onClick={
                fetchData
              }

              className="
                !rounded-xl
              "
            >
              Tải lại
            </Button>


            <Button
              type="primary"

              icon={
                <ScanOutlined />
              }

              loading={
                scanning
              }

              onClick={
                scanHistory
              }

              className="
                !rounded-xl
              "
            >
              Quét 10 đơn
            </Button>

          </div>

        </div>

      </div>


      <div
        className="
          mb-3

          rounded-xl

          bg-blue-50

          border
          border-blue-100

          p-3

          text-xs
          leading-5

          text-blue-700
        "
      >
        Chỉ hiển thị đơn bất thường đang
        <b> chưa xác nhận</b>.
        Xử lý xong đơn sẽ tự biến mất.
      </div>


      <Spin spinning={loading}>

        {data.length === 0 && !loading ? (

          <div
            className="
              bg-white

              rounded-2xl

              py-16
            "
          >

            <Empty
              description="Không còn đơn cần kiểm tra"
            />

          </div>

        ) : (

          <div
            className="
              flex
              flex-col

              gap-3

              max-w-[900px]

              mx-auto
            "
          >

            {data.map(
              item => {

                const issue =
                  issueInfo(
                    item?.issue_type
                  );

                const busy =
                  processing[
                    item?.order_id
                  ];


                return (

                  <div
                    key={
                      item?.audit_id ||
                      item?.order_id
                    }

                    className="
                      bg-white

                      rounded-2xl

                      border
                      border-gray-100

                      shadow-sm

                      overflow-hidden
                    "
                  >

                    {/* TOP */}

                    <div className="p-4">

                      <div
                        className="
                          flex
                          items-start
                          justify-between

                          gap-3
                        "
                      >

                        <div
                          className="
                            flex
                            items-start

                            gap-3

                            min-w-0
                          "
                        >

                          <div
                            className="
                              w-10
                              h-10

                              rounded-full

                              bg-blue-50

                              text-blue-600

                              flex
                              items-center
                              justify-center

                              flex-shrink-0
                            "
                          >
                            <UserOutlined />
                          </div>


                          <div className="min-w-0">

                            <div
                              className="
                                font-bold

                                text-[16px]

                                text-gray-900

                                break-words
                              "
                            >
                              {
                                item?.fullname ||
                                "Khách hàng"
                              }
                            </div>


                            <div
                              className="
                                mt-1

                                text-xs

                                text-gray-400
                              "
                            >
                              Mã đơn{" "}

                              <b
                                className="
                                  text-gray-700
                                "
                              >
                                #{item?.order_id}
                              </b>
                            </div>

                          </div>

                        </div>


                        <Tag
                          color={
                            issue.color
                          }

                          className="
                            !m-0

                            shrink-0
                          "
                        >
                          <WarningOutlined />

                          {" "}

                          {issue.label}
                        </Tag>

                      </div>


                      {/* INFO */}

                      <div
                        className="
                          mt-4

                          space-y-2
                        "
                      >

                        {item?.phone && (

                          <button
                            type="button"

                            onClick={() =>
                              copyText(
                                item.phone,
                                "Đã sao chép SĐT"
                              )
                            }

                            className="
                              w-full

                              rounded-xl

                              bg-blue-50

                              border
                              border-blue-100

                              px-3
                              py-3

                              flex
                              items-center
                              justify-between

                              gap-3

                              text-blue-700
                            "
                          >

                            <span
                              className="
                                flex
                                items-center

                                gap-2

                                font-semibold
                              "
                            >
                              <PhoneOutlined />

                              {item.phone}
                            </span>


                            <span
                              className="
                                text-xs
                                font-bold
                              "
                            >
                              <CopyOutlined />

                              {" "}Copy
                            </span>

                          </button>

                        )}


                        {item?.to_address && (

                          <div
                            className="
                              flex
                              items-start

                              gap-2

                              px-1

                              text-[13px]

                              text-gray-600
                            "
                          >
                            <EnvironmentOutlined
                              className="
                                mt-1
                              "
                            />

                            <span>
                              {item.to_address}
                            </span>
                          </div>

                        )}


                        <div
                          className="
                            flex
                            items-center

                            gap-2

                            px-1

                            text-xs

                            text-gray-400
                          "
                        >
                          <CalendarOutlined />

                          {
                            item?.create_at
                              ? dayjs(
                                  item.create_at
                                ).format(
                                  "DD/MM/YYYY HH:mm"
                                )
                              : "-"
                          }
                        </div>

                      </div>


                      {/* MONEY */}

                      <div
                        className="
                          grid
                          grid-cols-2

                          gap-2

                          mt-4
                        "
                      >

                        <div
                          className="
                            rounded-xl

                            bg-red-50

                            p-3
                          "
                        >

                          <div
                            className="
                              text-[10px]

                              text-gray-500
                            "
                          >
                            Cần thanh toán
                          </div>


                          <div
                            className="
                              mt-1

                              font-bold

                              text-red-600

                              text-[16px]
                            "
                          >
                            {
                              money(
                                item?.expected_amount ||
                                item?.total_price
                              )
                            }
                          </div>

                        </div>


                        <div
                          className="
                            rounded-xl

                            bg-gray-50

                            p-3
                          "
                        >

                          <div
                            className="
                              text-[10px]

                              text-gray-500
                            "
                          >
                            OCR đọc được
                          </div>


                          <div
                            className="
                              mt-1

                              font-bold

                              text-gray-900

                              text-[16px]
                            "
                          >
                            {
                              item?.detected_amount
                                ? money(
                                    item.detected_amount
                                  )
                                : "Không đọc được"
                            }
                          </div>

                        </div>

                      </div>


                      {/* DUPLICATE */}

                      {
                        item?.issue_type ===
                        "duplicate_image" && (

                          <div
                            className="
                              mt-3

                              rounded-xl

                              bg-red-50

                              border
                              border-red-100

                              px-3
                              py-2.5

                              text-xs

                              text-red-600
                            "
                          >
                            Ảnh này đã xuất hiện ở đơn{" "}

                            <b>
                              #{item?.duplicate_of_order_id}
                            </b>
                          </div>

                        )
                      }


                      {/* IMAGE */}

                      {item?.transfer_image && (

                        <div className="mt-3">

                          <div
                            className="
                              mb-2

                              text-xs

                              font-semibold

                              text-gray-500
                            "
                          >
                            Ảnh chuyển khoản
                          </div>


                          <div
                            className="
                              rounded-xl

                              overflow-hidden

                              bg-gray-50

                              text-center
                            "
                          >

                            <Image
                              src={
                                item.transfer_image
                              }

                              className="
                                !max-h-[420px]

                                !object-contain
                              "
                            />

                          </div>

                        </div>

                      )}

                    </div>


                    {/* ACTIONS */}

                    <div
                      className="
                        border-t
                        border-gray-100

                        bg-gray-50

                        p-3
                      "
                    >

                      <div
                        className="
                          text-[11px]

                          text-gray-400

                          mb-2

                          font-semibold
                        "
                      >
                        CHUYỂN TRẠNG THÁI
                      </div>


                      <div
                        className="
                          grid
                          grid-cols-1

                          sm:grid-cols-3

                          gap-2
                        "
                      >

                        <Button
                          type="primary"

                          size="large"

                          icon={
                            <CheckCircleOutlined />
                          }

                          loading={
                            busy === 1
                          }

                          disabled={
                            !!busy &&
                            busy !== 1
                          }

                          onClick={() =>
                            changeStatus(
                              item,
                              1
                            )
                          }

                          className="
                            !h-12

                            !rounded-xl

                            !font-bold
                          "
                        >
                          Thành công
                        </Button>


                        <Button
                          size="large"

                          icon={
                            <SendOutlined />
                          }

                          loading={
                            busy === 2
                          }

                          disabled={
                            !!busy &&
                            busy !== 2
                          }

                          onClick={() =>
                            changeStatus(
                              item,
                              2
                            )
                          }

                          className="
                            !h-12

                            !rounded-xl

                            !font-bold

                            !text-blue-600

                            !border-blue-200
                          "
                        >
                          Đang gửi hàng
                        </Button>


                        <Button
                          danger

                          size="large"

                          icon={
                            <CloseCircleOutlined />
                          }

                          loading={
                            busy === 3
                          }

                          disabled={
                            !!busy &&
                            busy !== 3
                          }

                          onClick={() =>
                            changeStatus(
                              item,
                              3
                            )
                          }

                          className="
                            !h-12

                            !rounded-xl

                            !font-bold
                          "
                        >
                          Hủy đơn
                        </Button>

                      </div>

                    </div>

                  </div>

                );
              }
            )}

          </div>

        )}

      </Spin>

    </div>
  );
}
